"use client"
import Link from "next/link"
import { Fragment, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Tooltip } from "@mui/material"
import { ArrowUpRightIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import ShareData from "./(components)/ShareData"
import Pagination from "./(components)/Pagination"
import Badges from "@/(front-desk)/user/compete/(components)/Badges"
import CertificateApply from "@/(front-desk)/user/compete/(components)/CertificateApply"
import stageCheck from "@/(front-desk)/user/compete/(utils)/stageCheck"
import dayjs from "dayjs"

export default function Home() {

    const account = useSelector((state) => state.account)
    const global = useSelector((state) => state.global)
    const compete = useSelector((state) => state.compete)
    const [ popup, setPopup ] = useState(null)
    const [ applyId, setApplyId ] = useState(null)
    const registration = compete.stages?.find(stage => stage.shortcut === "registration")

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
    }, [])

    if (compete.submitted?.results) return (
        <ShareData popup={popup} setPopup={setPopup}>
            { compete.submitted.results.length === 0 &&
                <>
                    <div className="w-40 mt-4 md:w-60 md:mt-20 mx-auto">
                    </div>
                    <div className="my-4 text-center text-black-900 text-2xl font-semibold">
                        {registration?.current_status === "not-started" && "參賽報名即將開始！"}
                        {registration?.current_status === "ongoing" && "放視大賞參賽報名起跑囉！"}
                        {registration?.current_status === "done" && "參賽報名已截止囉！"}
                    </div>
                    <p className="mt-4 text-center text-black-700 font-light whitespace-pre-wrap">
                        {registration?.current_status === "not-started" && `${global.currentCompeteYear} 年度競賽報名期間為 ${dayjs(registration.start).format('MM 月 DD 日 HH:mm')} 開始至 ${dayjs(registration.end).format('MM 月 DD 日 HH:mm')} 止。`}
                        {registration?.current_status === "ongoing" && "趕快詳閱參賽辦法，建立作品參賽資料吧！\n若你是組員，請務必要求組長把你加入參賽團隊成員。"}
                        {registration?.current_status === "done" && `參賽報名已於 ${dayjs(registration.end).format('YYYY 年 MM 月 DD 日 HH:mm')} 截止`}
                    </p>

                    { registration?.current_status === "ongoing" &&
                        <button
                            className="mx-auto btn btn-black btn-round h-fit whitespace-nowrap mt-10 mb-24"
                            onClick={() => { setPopup('sign-up') }}
                        >＋ 新增報名</button>
                    }
                </>
            }
            { compete.submitted.results.length > 0 &&
                <>
                    { account.data && account.data.unverified_email
                        ?
                        <button className="btn btn-black btn-round h-fit mt-10" disabled={true}>請完成信箱驗證</button>
                        :
                        <>
                        { registration?.current_status === "ongoing" &&
                            <button
                                className="btn btn-black btn-round h-fit whitespace-nowrap mt-10"
                                onClick={() => { setPopup('sign-up') }}
                            >＋ 新增報名</button>
                        }
                        </>
                    }
                    <div className="flex flex-wrap w-[calc(100%+32px)] mx-[-16px] mt-10">
                        { compete.submitted.results.map(data => (
                            <div key={data.id} className="m-4 rounded-lg bg-white shadow w-[calc(100%-32px)] min-w-[17.5rem] md:w-[calc(100%/2-32px)] lg:w-[calc(100%/4-32px)]">
                                <div className="relative w-full pt-[calc(152/268*100%)] rounded-t-lg overflow-hidden">
                                    <img src={data.cover} className="absolute top-0 left-0 w-full h-full object-cover" alt={`${data.project_name} ${data.project_name_en}`}/>
                                </div>

                                <div className="relative p-6">
                                    <Badges
                                        className="absolute bottom-[100%] mb-[-1rem]"
                                        awards={data.awards}
                                        pending={stageCheck(compete.stages, 'review-ongoing')}
                                        collapse
                                    />

                                    <h4 className="text-[1.125rem] text-black-900 font-bold mb-1">{data.project_name}</h4>

                                    <p className="text-[0.875rem] text-black-900 font-light mb-4">{data.category.name} - {data.subcategory.name}</p>

                                    <div className="flex flex-col gap-1 font-light text-black-700 text-[0.875rem] mb-4">
                                        <div className="flex gap-4">
                                            <span>送件時間</span>
                                            <span>{dayjs(data.applicant_update_time).format("YYYY/MM/DD HH:mm")}</span>
                                        </div>
                                        <div className="flex gap-4">
                                            <span>參賽編號</span>
                                            <span>{data.serial_no}</span>
                                        </div>
                                        <div className="flex gap-2.5">
                                            <span className="flex items-center gap-1">
                                                邀請碼
                                                <Tooltip
                                                    arrow
                                                    placement="top"
                                                    title="此邀請碼供「決選作品展出攤位」申請者使用。如本作品將併入校系團體參展展出，可忽略此訊息。"
                                                    classes={{
                                                        tooltip: 'px-3 py-1 bg-main-700 w-[15rem] text-[0.875rem] font-normal',
                                                        arrow: 'text-main-700'
                                                    }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" className="tooltip-handle w-4">
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M0.629883 7.99992C0.629883 3.95144 3.92685 0.678711 7.97534 0.678711C12.0238 0.678711 15.2965 3.95144 15.2965 7.99992C15.2965 12.0484 12.0238 15.3454 7.97534 15.3454C3.92685 15.3454 0.629883 12.0484 0.629883 7.99992ZM1.72079 7.99992C1.72079 11.4423 4.53291 14.2545 7.97534 14.2545C11.4178 14.2545 14.2056 11.4423 14.2056 7.99992C14.2056 4.5575 11.4178 1.74538 7.97534 1.74538C4.53291 1.74538 1.72079 4.5575 1.72079 7.99992ZM7.97472 4.87264H7.95048C7.65957 4.87264 7.3929 5.11506 7.3929 5.43022C7.3929 5.74537 7.63533 5.98779 7.95048 5.98779H7.97472C8.26563 5.98779 8.5323 5.74537 8.5323 5.43022C8.5323 5.11506 8.28987 4.87264 7.97472 4.87264ZM7.97522 6.78784C7.68431 6.78784 7.41764 7.03027 7.41764 7.34542V11.0788C7.41764 11.3697 7.66007 11.6363 7.97522 11.6363C8.26613 11.6363 8.5328 11.3939 8.5328 11.0788V7.32118C8.5328 7.03027 8.29037 6.78784 7.97522 6.78784Z" fill="#666666"/>
                                                    </svg>
                                                </Tooltip>
                                            </span>
                                            <span>{data.code}</span>
                                        </div>
                                        { data.exhibition_project_serial_no &&
                                            <div className="flex gap-4">
                                                <span>參展申請</span>
                                                <span>{data.exhibition_project_serial_no}</span>
                                            </div>
                                        }
                                        { data.exhibition_project_booth_no &&
                                            <div className="flex gap-4">
                                                <span>攤位編號</span>
                                                <span>{data.exhibition_project_booth_no}</span>
                                            </div>
                                        }

                                        {/* <div className="">作品成員：{
                                            data.applicants.reduce((filtered, option) => {
                                                filtered.push(option.name)
                                                return filtered
                                            }, []).join(", ")
                                        }</div> */}
                                    </div>


                                    <div className="flex flex-col gap-2">
                                        { stageCheck(compete.stages, 'registration-ongoing') && // 報名階段
                                            <Link href={`/user/compete/detail?id=${data.id}`} className="btn-black-200">
                                                檢視報名資料
                                                <ArrowUpRightIcon />
                                            </Link>
                                        }
                                        { stageCheck(compete.stages, 'shortlist-end') && // 決選公布後
                                            <Link href={`/user/compete/detail?id=${data.id}&tab=feedback`} className="btn-black-200">
                                                查看評審及觀眾回饋
                                                <ChevronRightIcon />
                                            </Link>
                                        }
                                        {/* { stageCheck(compete.stages, 'certificate-application-start') && // 開放證明申請後
                                            <div className="card-dropdown btn-outline-black-300">
                                                各類證明、獎狀申請
                                                <ChevronDownIcon />

                                                <div className="card-dropdown-content">
                                                    <a
                                                        className="item flex items-center justify-between px-5 py-2 font-normal"
                                                        href={typeformLink(data, "competition-participation")}
                                                        target="_blank"
                                                        title="另開新視窗前往"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <DocumentCheckIcon className="w-4" />
                                                            申請參賽證明
                                                        </div>
                                                        <ArrowTopRightOnSquareIcon className="w-4" />
                                                    </a>
                                                    <a
                                                        className="item flex items-center justify-between px-5 py-2 font-normal"
                                                        href={typeformLink(data, "competition-advisor")}
                                                        target="_blank"
                                                        title="另開新視窗前往"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <DocumentCheckIcon className="w-4" />
                                                            申請指導老師證明
                                                        </div>
                                                        <ArrowTopRightOnSquareIcon className="w-4" />
                                                    </a>
                                                    { data.awards?.length > 0 && data.awards?.filter(a => a.award_type.shortcut === "prelim-nominee").length === 0 &&
                                                        <a
                                                            className="item flex items-center justify-between px-5 py-2 font-normal"
                                                            href={typeformLink(data, "exhibition-participation")}
                                                            target="_blank"
                                                            title="另開新視窗前往"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <DocumentCheckIcon className="w-4" />
                                                                申請參展證明
                                                            </div>
                                                            <ArrowTopRightOnSquareIcon className="w-4" />
                                                        </a>
                                                    }
                                                    { data.awards?.length > 0 &&
                                                        <a
                                                            className="item flex items-center justify-between px-5 py-2 font-normal"
                                                            href={typeformLink(data, "competition-award")}
                                                            target="_blank"
                                                            title="另開新視窗前往"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <TrophyIcon className="w-4" />
                                                                申請獲獎證書
                                                            </div>
                                                            <ArrowTopRightOnSquareIcon className="w-4" />
                                                        </a>
                                                    }
                                                </div>
                                            </div>
                                        } */}
                                        { stageCheck(compete.stages, 'certificate-application-start') && data.user_is_captain &&  // 開放證明申請後，且使用者為組長
                                            <Fragment>
                                                <button
                                                    className="btn-black-200"
                                                    onClick={() => { setApplyId(data.id) }}
                                                >
                                                    申請參賽證明／獎狀
                                                    <ChevronRightIcon />
                                                </button>
                                                { applyId === data.id &&
                                                    <CertificateApply
                                                        id={applyId}
                                                        setPopup={setApplyId}
                                                    />
                                                }
                                            </Fragment>
                                        }
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <Pagination
                        type="submitted"
                        current={compete.submitted.current_page}
                        total={compete.submitted.total_pages}
                    />
                </>
            }
        </ShareData>
    )
}
